import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ArrowLeft, ArrowRight, Save } from "lucide-react";
import { COMMODITIES } from "@/data/commodities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "zorem-mandate-draft";

const STEP_META = [
  { id: "discovery", title: "Discovery", quote: "A confidential conversation to understand the mandate, the material and the constraints." },
  { id: "cis", title: "Client Information Sheet", quote: "Formal details and contact channels — the start of the paper trail." },
  { id: "capacity", title: "Capacity / Funds Verification", quote: "Proof of product or proof of funds." },
  { id: "diligence", title: "Due Diligence", quote: "KYC, AML, origin checks, sanctions screening and performance history." },
  { id: "structure", title: "Structure & Contract", quote: "Clear commercial terms, instruments and milestones agreed in writing." },
  { id: "execution", title: "Review & Submit", quote: "Trade activated, monitored and closed against the agreed sequence." },
];

const inputCls =
  "w-full bg-[#0A0A0B] card-line px-4 py-4 text-base text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#C9A227]/60 transition-colors";
const labelCls = "block font-mono2 text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2";

const Field = ({ label, hint, children, testid }) => (
  <div className="mb-5" data-testid={testid}>
    <label className="block">
      <span className={labelCls}>{label}</span>
      {children}
    </label>
    {hint && <p className="mt-1.5 text-xs text-zinc-600">{hint}</p>}
  </div>
);

const Tooltip = ({ text, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex ml-2 align-middle">
      <button type="button" data-testid={testid} onClick={() => setShow(!show)} aria-label="About this step"
        className="text-[#C9A227] hover:text-[#E8C85A] transition-colors">
        <Info size={15} />
      </button>
      {show && (
        <span className="absolute z-20 left-6 -top-2 w-64 bg-[#1C1C22] card-line px-4 py-3 text-xs text-zinc-300 leading-relaxed normal-case tracking-normal" data-testid={`${testid}-popup`}>
          “{text}”<span className="block mt-1.5 text-[10px] text-zinc-500">— Zorem Commodities Company Profile, 2026</span>
        </span>
      )}
    </span>
  );
};

const Option = ({ selected, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} aria-pressed={selected}
    className={`px-5 py-3.5 text-sm tracking-wide transition-colors card-line ${selected ? "bg-[#C9A227] text-[#0A0A0B] border-[#C9A227] font-medium" : "bg-[#0A0A0B] text-zinc-400 hover:bg-[#16161A] hover:text-zinc-200"}`}>
    {children}
  </button>
);

const EMPTY = {
  role: "", commodity: "", volume: "", constraints: "",
  name: "", company: "", country: "", email: "", phone: "",
  instrument: "", bank: "", origin: "", sourceName: "", assayAvailable: "",
  uboConfirm: false, sanctionsConsent: false, accurate: false,
  settlement: "", timeline: "", hub: "",
};

export default function Mandate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
    } catch { return EMPTY; }
  });

  useEffect(() => { document.title = "Submit a Mandate — Zorem Commodities LLC"; }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
      setSavedAt(new Date());
    }, 500);
    return () => clearTimeout(t);
  }, [data]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const meta = STEP_META[step];

  const stepValid = useMemo(() => {
    switch (step) {
      case 0: return !!data.role && !!data.commodity;
      case 1: return !!data.name && /.+@.+\..+/.test(data.email);
      case 2:
        if (data.role === "buyer") return !!data.instrument;
        if (data.role === "seller") return !!data.origin;
        return true;
      case 3: return data.uboConfirm && data.sanctionsConsent && data.accurate;
      case 4: return !!data.timeline;
      default: return true;
    }
  }, [step, data]);

  const submit = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/mandates`, {
        role: data.role, commodity: data.commodity, volume: data.volume,
        name: data.name, email: data.email, company: data.company,
        country: data.country, phone: data.phone,
        details: {
          constraints: data.constraints, instrument: data.instrument, bank: data.bank,
          origin: data.origin, sourceName: data.sourceName, assayAvailable: data.assayAvailable,
          settlement: data.settlement, timeline: data.timeline, hub: data.hub,
        },
        consents: ["ubo", "sanctions", "accuracy"],
      });
      localStorage.removeItem(DRAFT_KEY);
      navigate("/mandate/success", { state: { reference: res.data.reference, name: data.name } });
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally { setSending(false); }
  };

  const next = () => { if (stepValid && step < 5) { setStep(step + 1); window.scrollTo(0, 0); } };

  return (
    <div data-testid="mandate-page" className="bg-[#0A0A0B] min-h-screen pb-32">
      <div className="pt-32 pb-10 bg-[#121215] hairline-b sticky top-[6.25rem] z-30 bg-opacity-95 backdrop-blur-xl">
        <div className="max-w-xl mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#C9A227]" data-testid="mandate-step-label">
              Step {step + 1} of 6 — {meta.title}
            </span>
            {savedAt && (
              <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500" data-testid="mandate-draft-saved">
                <Save size={11} /> Draft saved
              </span>
            )}
          </div>
          <div className="h-1 bg-[#1C1C22] overflow-hidden">
            <motion.div className="h-full bg-gradient-to-r from-[#997A19] to-[#D4AF37]"
              animate={{ width: `${((step + 1) / 6) * 100}%` }} transition={{ duration: 0.4 }} data-testid="mandate-progress" />
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-12">
        <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-wide text-white" data-testid="mandate-step-title">
          {meta.title}
          <Tooltip text={meta.quote} testid={`mandate-tooltip-${meta.id}`} />
        </h1>
        {step === 0 && (
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
            This form mirrors exactly how Zorem structures live transactions: discovery, formal
            client information, capacity verification, due diligence, written structure, and
            execution. It takes about three minutes, saves your draft automatically, and starts
            the paper trail that protects both sides. Nothing you enter is shared beyond the
            verification process described in our compliance framework.
          </p>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="mt-8">
            {step === 0 && (
              <>
                <Field label="I am a…" testid="mandate-field-role">
                  <div className="flex flex-wrap gap-3">
                    {[["buyer", "Buyer"], ["seller", "Seller"], ["partner", "Partner"]].map(([v, l]) => (
                      <Option key={v} testid={`mandate-role-${v}`} selected={data.role === v} onClick={() => set("role", v)}>{l}</Option>
                    ))}
                  </div>
                </Field>
                <Field label="Commodity of interest" testid="mandate-field-commodity">
                  <select value={data.commodity} onChange={(e) => set("commodity", e.target.value)} className={inputCls} data-testid="mandate-select-commodity">
                    <option value="" disabled>Select a commodity</option>
                    {COMMODITIES.map((c) => <option key={c.slug} value={c.name}>{c.name} — {c.spec}</option>)}
                    <option value="Other">Other / multiple</option>
                  </select>
                </Field>
                <Field label="Indicative volume" testid="mandate-field-volume">
                  <input value={data.volume} onChange={(e) => set("volume", e.target.value)} placeholder="e.g. 25 kg Au monthly" className={inputCls} data-testid="mandate-input-volume" />
                </Field>
                <Field label="Constraints or context (optional)" testid="mandate-field-constraints">
                  <textarea rows={3} value={data.constraints} onChange={(e) => set("constraints", e.target.value)} placeholder="Timeline, corridor, instrument constraints…" className={inputCls} data-testid="mandate-input-constraints" />
                </Field>
              </>
            )}

            {step === 1 && (
              <>
                <Field label="Full name *" testid="mandate-field-name">
                  <input value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Principal or authorized signatory" className={inputCls} data-testid="mandate-input-name" />
                </Field>
                <Field label="Company / Entity" testid="mandate-field-company">
                  <input value={data.company} onChange={(e) => set("company", e.target.value)} placeholder="Registered entity name" className={inputCls} data-testid="mandate-input-company" />
                </Field>
                <Field label="Country of incorporation / residence" testid="mandate-field-country">
                  <input value={data.country} onChange={(e) => set("country", e.target.value)} placeholder="Country" className={inputCls} data-testid="mandate-input-country" />
                </Field>
                <Field label="Institutional email *" testid="mandate-field-email">
                  <input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="name@company.com" className={inputCls} data-testid="mandate-input-email" />
                </Field>
                <Field label="Phone / WhatsApp" testid="mandate-field-phone">
                  <input value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 …" className={inputCls} data-testid="mandate-input-phone" />
                </Field>
              </>
            )}

            {step === 2 && data.role === "buyer" && (
              <>
                <Field label="Intended payment instrument *" testid="mandate-field-instrument">
                  <div className="flex flex-wrap gap-3">
                    {["SBLC", "DLC", "MT103 Wire", "Escrow", "To be advised"].map((v) => (
                      <Option key={v} testid={`mandate-instrument-${v.toLowerCase().replace(/\s+/g, "-")}`} selected={data.instrument === v} onClick={() => set("instrument", v)}>{v}</Option>
                    ))}
                  </div>
                </Field>
                <Field label="Issuing bank (optional)" hint="Confirmed directly with the institution before activation." testid="mandate-field-bank">
                  <input value={data.bank} onChange={(e) => set("bank", e.target.value)} placeholder="Bank name" className={inputCls} data-testid="mandate-input-bank" />
                </Field>
              </>
            )}
            {step === 2 && data.role === "seller" && (
              <>
                <Field label="Country of origin *" testid="mandate-field-origin">
                  <input value={data.origin} onChange={(e) => set("origin", e.target.value)} placeholder="e.g. DRC, Rwanda, Senegal" className={inputCls} data-testid="mandate-input-origin" />
                </Field>
                <Field label="Mine / cooperative / refinery" testid="mandate-field-source">
                  <input value={data.sourceName} onChange={(e) => set("sourceName", e.target.value)} placeholder="Source name" className={inputCls} data-testid="mandate-input-source" />
                </Field>
                <Field label="Is an assay report available?" testid="mandate-field-assay">
                  <div className="flex gap-3">
                    {[["yes", "Yes"], ["no", "No"], ["pending", "In progress"]].map(([v, l]) => (
                      <Option key={v} testid={`mandate-assay-${v}`} selected={data.assayAvailable === v} onClick={() => set("assayAvailable", v)}>{l}</Option>
                    ))}
                  </div>
                </Field>
              </>
            )}
            {step === 2 && data.role === "partner" && (
              <Field label="Your region and focus" testid="mandate-field-partner">
                <textarea rows={4} value={data.bank} onChange={(e) => set("bank", e.target.value)} placeholder="Corridors, commodities, and the kind of partnership you have in mind…" className={inputCls} data-testid="mandate-input-partner" />
              </Field>
            )}

            {step === 3 && (
              <div className="space-y-3">
                {[
                  ["uboConfirm", "Beneficial ownership (UBO) information can be provided on request", "mandate-consent-ubo"],
                  ["sanctionsConsent", "I consent to sanctions screening against OFAC, EU, UN and UK HMT lists", "mandate-consent-sanctions"],
                  ["accurate", "The information provided in this mandate is accurate to my knowledge", "mandate-consent-accurate"],
                ].map(([k, l, tid]) => (
                  <button key={k} type="button" data-testid={tid} onClick={() => set(k, !data[k])} aria-pressed={data[k]}
                    className={`w-full flex items-start gap-4 px-4 py-4 text-left transition-colors card-line ${data[k] ? "bg-[#1C1C22]" : "bg-[#0A0A0B] hover:bg-[#16161A]"}`}>
                    <span className={`mt-0.5 w-5 h-5 shrink-0 card-line flex items-center justify-center ${data[k] ? "bg-[#C9A227] border-[#C9A227]" : ""}`}>
                      {data[k] && <span className="text-[#0A0A0B] text-xs font-bold">✓</span>}
                    </span>
                    <span className={`text-sm leading-relaxed ${data[k] ? "text-[#E8C85A]" : "text-zinc-400"}`}>{l}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 4 && (
              <>
                <Field label="Settlement preference" testid="mandate-field-settlement">
                  <div className="flex flex-wrap gap-3">
                    {["Documents against payment", "Refinery assay on arrival", "Escrow release", "To be structured"].map((v) => (
                      <Option key={v} testid={`mandate-settlement-${v.toLowerCase().replace(/\s+/g, "-")}`} selected={data.settlement === v} onClick={() => set("settlement", v)}>{v}</Option>
                    ))}
                  </div>
                </Field>
                <Field label="Target timeline *" testid="mandate-field-timeline">
                  <div className="flex flex-wrap gap-3">
                    {["Immediately", "30 days", "90 days", "Exploratory"].map((v) => (
                      <Option key={v} testid={`mandate-timeline-${v.toLowerCase().replace(/\s+/g, "-")}`} selected={data.timeline === v} onClick={() => set("timeline", v)}>{v}</Option>
                    ))}
                  </div>
                </Field>
                <Field label="Preferred delivery hub" testid="mandate-field-hub">
                  <select value={data.hub} onChange={(e) => set("hub", e.target.value)} className={inputCls} data-testid="mandate-select-hub">
                    <option value="" disabled>Select a hub</option>
                    {["Dubai", "London", "Zurich", "New York", "Miami", "Hong Kong", "Origin country"].map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </Field>
              </>
            )}

            {step === 5 && (
              <div className="card-line bg-[#121215] p-6" data-testid="mandate-review">
                <h3 className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#C9A227] mb-5">Review your mandate</h3>
                <dl className="space-y-3 text-sm">
                  {[
                    ["Role", data.role], ["Commodity", data.commodity], ["Volume", data.volume],
                    ["Name", data.name], ["Company", data.company], ["Country", data.country],
                    ["Email", data.email], ["Phone", data.phone],
                    data.role === "buyer" ? ["Instrument", data.instrument] : null,
                    data.role === "buyer" && data.bank ? ["Bank", data.bank] : null,
                    data.role === "seller" ? ["Origin", data.origin] : null,
                    data.role === "seller" && data.sourceName ? ["Source", data.sourceName] : null,
                    ["Settlement", data.settlement], ["Timeline", data.timeline], ["Delivery hub", data.hub],
                  ].filter(Boolean).map(([k, v]) => v ? (
                    <div key={k} className="flex justify-between gap-6">
                      <dt className="text-zinc-500 shrink-0">{k}</dt>
                      <dd className="text-zinc-200 text-right capitalize">{v}</dd>
                    </div>
                  ) : null)}
                </dl>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {!stepValid && step !== 5 && (
          <p className="mt-4 text-xs text-zinc-600" data-testid="mandate-hint">Complete the required fields to continue.</p>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 z-30 bg-[#0A0A0B]/95 backdrop-blur-xl hairline-t">
        <div className="max-w-xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          {step > 0 ? (
            <button type="button" data-testid="mandate-back" onClick={() => setStep(step - 1)}
              className="inline-flex items-center gap-2 px-5 py-3.5 text-[11px] uppercase tracking-[0.2em] text-zinc-400 card-line hover:text-[#D4AF37] hover:bg-[#16161A] transition-colors">
              <ArrowLeft size={14} /> Back
            </button>
          ) : (
            <Link to="/" data-testid="mandate-cancel" className="inline-flex items-center gap-2 px-5 py-3.5 text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors">
              Cancel
            </Link>
          )}
          {step < 5 ? (
            <button type="button" data-testid="mandate-next" onClick={next} disabled={!stepValid}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-colors disabled:opacity-40">
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button type="button" data-testid="mandate-submit" onClick={submit} disabled={sending}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-colors disabled:opacity-40">
              {sending ? "Submitting…" : "Submit Mandate"} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
